import { useRef, useState } from 'react';
import { productCategories } from '@/mocks/products';
import type { Product } from './BagContext';
import QtyStepper from './QtyStepper';
import useOverlay from './useOverlay';
import useOverlayMotion from './useOverlayMotion';

type Props = { product: Product; onClose: () => void; onAdd: (qty: number) => void };

export default function QuickView({ product, onClose, onAdd }: Props) {
  const [qty, setQty] = useState(1);
  const closeFn = useRef<() => void>(onClose);
  const panelRef = useOverlay(() => closeFn.current());
  const { backdropRef, requestClose } = useOverlayMotion('modal', onClose, panelRef);
  closeFn.current = requestClose;
  const cat = productCategories.find((c) => c.id === product.category)?.label;

  return (
    <div className="ff-sans fixed inset-0 z-[100] flex items-end md:items-center justify-center md:p-4 text-[#111111]" onClick={requestClose}>
      <div ref={backdropRef} aria-hidden="true" className="absolute inset-0 bg-black/40"></div>
      <div
        ref={panelRef}
        data-lenis-prevent
        role="dialog"
        aria-modal="true"
        aria-labelledby="qv-title"
        className="relative bg-white w-full md:max-w-[960px] max-h-[92vh] overflow-y-auto grid grid-cols-1 md:grid-cols-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full h-[52vh] md:h-[600px]">
          <img src={product.image} alt={`${product.name} — Fleur & Form`} title={`${product.name} — Fleur & Form`} className="w-full h-full object-cover object-center" />
        </div>
        <div className="flex flex-col p-5 md:p-10 min-w-0">
          <button type="button" data-autofocus onClick={requestClose} aria-label="Close product details" className="self-end w-8 h-8 flex items-center justify-center cursor-pointer whitespace-nowrap hover:opacity-50">
            <i className="ri-close-line text-xl"></i>
          </button>
          <span className="text-[11px] text-black/40 mt-2 md:mt-4">{product.no} / {cat}</span>
          <h3 id="qv-title" className="font-normal tracking-[-0.03em] text-[30px] md:text-[40px] leading-none mt-3">{product.name}</h3>
          <p className="text-[14px] mt-3">${product.price}</p>
          <p className="ff-serif italic text-[16px] md:text-[17px] text-black/60 mt-6 md:mt-8 leading-snug">{product.note}</p>
          <div className="mt-8 md:mt-auto pt-0 md:pt-10 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-black/50">Quantity</span>
              <QtyStepper value={qty} onChange={setQty} label="Quantity" />
            </div>
            <button type="button" onClick={() => onAdd(qty)} className="w-full h-12 bg-[#111111] text-white text-[13px] tracking-wide cursor-pointer whitespace-nowrap hover:bg-black/80 transition-colors">
              Add to Bag — ${product.price * qty}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}